/**
 * 차량 기본정보 관리 (v2.61.0) — [사내결재 문서함] > [지출 현황] > 차량별 집계 옆 [🚗 차량 관리].
 *
 * 왜 필요한가: 법인카드 [사용대상] 칸은 **자유 입력**이라 같은 차가 여러 표기로 갈라진다
 *   (「6526」 과 「194호 6526」, 「레이EV 05하2340」 과 「05하 2340」).
 *   그래서 지출내역 글자는 그대로 두고, 여기 등록한 차량으로 **묶어서** 비용을 붙인다.
 *
 * ⭐ 이 화면의 핵심은 아래쪽 **「어느 차에도 안 붙은 표기」** 다.
 *   여기가 비어 있어야 차량별 합계 = 차량유지비 합계다. 뭔가 떠 있으면 그 금액이
 *   차량별 집계에서 빠져 있다는 뜻이므로, 해당 차량의 [별칭]에 그 글자를 넣어 주면 된다.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/expense-api';
import { DraggableModal } from './DraggableModal';
import { useDirtyClose } from '../lib/useDirtyClose';
import { LoadingLines } from './ui/Skeleton';

const fmt = (n: number) => n.toLocaleString('ko-KR');

/** 빈 입력값 — [+ 차량 추가] 눌렀을 때 */
const EMPTY: api.VehicleInput = {
  name: '', plateNo: '', model: '', status: '사용중', aliases: [], sortOrder: 0, note: '',
  assignedEmpId: null,
};

export function VehicleManageModal({ onClose }: { onClose: () => void }) {
  // 작성 중 닫힘 방지 — ESC·[×] 가 requestClose 를 거친다 (v2.163.0).
  //   차량 줄은 [저장] 으로 그때그때 저장되므로 저장 성공 시 resetDirty 로 물음을 푼다.
  const { markDirty, resetDirty, requestClose } = useDirtyClose(onClose);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['expense', 'vehicles'],
    queryFn: api.listVehicles,
  });

  /** 편집 중인 차량 — null = 편집 안 함, id=0 = 새로 추가 */
  const [editId, setEditId] = useState<number | null>(null);
  const [draft, setDraft] = useState<api.VehicleInput>(EMPTY);
  const [aliasText, setAliasText] = useState('');
  const [err, setErr] = useState('');

  /**
   * 직원 목록 (v2.93.0) — 담당 직원 칸의 후보. 사람 아닌 계정(공용·시스템)은 서버가 뺀다.
   * 편집 권한이 없어도 목록에 이름을 보여야 하므로 항상 불러온다(작고 캐시된다).
   */
  const usersQuery = useQuery({
    queryKey: ['expense', 'users'],
    queryFn: api.listPortalUsers,
    staleTime: 300_000,
  });
  const users = usersQuery.data ?? [];
  const empName = (id: string): string | null => users.find((u) => u.userId === id)?.name ?? null;

  const rows = data?.rows ?? [];
  const unmatched = data?.unmatched ?? [];
  const canEdit = data?.canEdit ?? false;
  // 금액 열람 = 경영지원·대표·admin (v2.84.0) — 아니면 서버가 금액을 null 로 준다
  const canViewAmounts = data?.canViewAmounts ?? false;

  const total = useMemo(
    () => rows.reduce((s, r) => s + (r.matchedAmount ?? 0), 0),
    [rows],
  );

  /** 별칭은 쉼표로 끊어 입력받는다 — 화면에 배열을 그대로 보이면 읽기 어렵다 */
  function startEdit(r: api.VehicleRow | null) {
    setErr('');
    if (r === null) {
      setEditId(0); setDraft(EMPTY); setAliasText('');
      return;
    }
    setEditId(r.id);
    setDraft({
      name: r.name, plateNo: r.plateNo, model: r.model, status: r.status,
      aliases: r.aliases, sortOrder: r.sortOrder, note: r.note,
      assignedEmpId: r.assignedEmpId,
    });
    setAliasText(r.aliases.join(', '));
  }

  const body = (): api.VehicleInput => ({
    ...draft,
    aliases: aliasText.split(',').map((s) => s.trim()).filter((s) => s !== ''),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (editId === 0) await api.createVehicle(body());
      else await api.updateVehicle(editId as number, body());
    },
    onSuccess: async () => {
      setEditId(null); setErr('');
      await qc.invalidateQueries({ queryKey: ['expense'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteVehicle(id),
    onSuccess: async () => {
      setEditId(null); setErr('');
      await qc.invalidateQueries({ queryKey: ['expense'] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  // 저장이 끝나 목록이 바뀌면 편집칸을 닫아 둔다
  useEffect(() => { if (save.isSuccess) { setEditId(null); resetDirty(); } }, [save.isSuccess, resetDirty]);

  return (
    <DraggableModal onClose={requestClose} initialWidth={860} initialHeight={640} closeOnEsc={false}
      bodyClassName="flex-1 min-h-0 overflow-hidden flex flex-col">
      <div className="dm-drag-handle flex items-center justify-between px-4 py-2.5 border-b border-slate-200 cursor-move bg-slate-50">
        <h2 className="ui-section-title">🚗 차량 기본정보</h2>
        <button type="button" onClick={requestClose}
          className="ui-close">×</button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4" onInput={markDirty} onChange={markDirty}>
        <p className="text-xs text-slate-500 leading-relaxed">
          법인카드 [사용대상] 칸은 자유 입력이라 같은 차가 여러 표기로 적힙니다.
          여기 등록한 <b>차량번호</b>와 <b>별칭</b>으로 그 글자들을 묶어 <b>차량별 유지비</b>를 냅니다.
          지출내역 원문은 바뀌지 않습니다.
        </p>

        {err && (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>
        )}

        {/* ── 등록 차량 ── */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <h3 className="ui-subsection-title text-xs">
              등록 차량 {rows.length}대
              {canViewAmounts && (
                <span className="font-normal text-slate-400 ml-2">
                  붙은 유지비 합계 {fmt(total)}원
                </span>
              )}
            </h3>
            {canEdit && editId === null && (
              <button type="button" onClick={() => startEdit(null)}
                className="btn btn-outline btn-sm">
                + 차량 추가
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="text-xs text-slate-500 py-6 text-center"><LoadingLines /></div>
          ) : (
            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left font-medium px-2 py-1.5 min-w-[110px]">차량</th>
                    <th className="text-left font-medium px-2 py-1.5 min-w-[80px]">차종</th>
                    <th className="text-left font-medium px-2 py-1.5 min-w-[64px]">상태</th>
                    {/* 담당 직원 (v2.93.0) — 법인카드 작성 때 사용대상·비고 자동 입력의 근거 */}
                    <th className="text-left font-medium px-2 py-1.5 min-w-[80px]">담당 직원</th>
                    <th className="text-left font-medium px-2 py-1.5 min-w-[140px]">지출내역 표기(별칭)</th>
                    {/* 금액 열람 권한이 없으면 건수만 보이므로 머리글도 맞춘다 (v2.84.0) */}
                    <th className="text-right font-medium px-2 py-1.5 min-w-[120px]">{canViewAmounts ? '붙은 유지비' : '붙은 내역'}</th>
                    {canEdit && <th className="px-2 py-1.5 w-[56px]" />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100 align-top">
                      <td className="px-2 py-1.5 text-slate-800 font-medium">{r.name}</td>
                      <td className="px-2 py-1.5 text-slate-600">{r.model || '-'}</td>
                      <td className="px-2 py-1.5">
                        <span className={r.status === '계약만료'
                          ? 'text-slate-500 bg-slate-100 rounded px-1.5 py-0.5'
                          : 'text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5'}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-slate-700">
                        {r.assignedEmpId
                          ? (empName(r.assignedEmpId) ?? r.assignedEmpId)
                          : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-1.5 text-slate-500">
                        {r.matchedTargets.length > 0
                          ? r.matchedTargets.join(' · ')
                          : <span className="text-slate-300">아직 붙은 내역 없음</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right num text-slate-700">
                        {/* 금액은 경영지원·대표·admin 만 (v2.84.0) — 일반 직원은 건수만 */}
                        {r.matchedAmount != null && <>{fmt(r.matchedAmount)}원</>}
                        <span className="text-slate-400"> ({r.matchedCount}건)</span>
                      </td>
                      {canEdit && (
                        <td className="px-2 py-1.5 text-right">
                          <button type="button" onClick={() => startEdit(r)}
                            className="text-blue-600 hover:underline">수정</button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── 편집칸 ── */}
        {canEdit && editId !== null && (
          <div className="border border-blue-200 bg-blue-50/40 rounded p-3 space-y-2">
            <h3 className="ui-subsection-title text-xs">
              {editId === 0 ? '차량 추가' : `차량 수정 — ${draft.name}`}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <label className="ui-label">
                차량 이름 <span className="text-rose-500">*</span>
                <input className="ui-input ui-input-sm mt-0.5 w-full"
                  placeholder="174호 2716"
                  value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              </label>
              <label className="ui-label">
                차량번호 (매칭용 · 없으면 비움)
                <input className="ui-input ui-input-sm mt-0.5 w-full"
                  placeholder="174호 2716"
                  value={draft.plateNo} onChange={(e) => setDraft({ ...draft, plateNo: e.target.value })} />
              </label>
              <label className="ui-label">
                차종
                <input className="ui-input ui-input-sm mt-0.5 w-full"
                  placeholder="카니발"
                  value={draft.model} onChange={(e) => setDraft({ ...draft, model: e.target.value })} />
              </label>
              <label className="ui-label">
                상태
                <select className="ui-input ui-input-sm mt-0.5 w-full"
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value as api.VehicleStatus })}>
                  {api.VEHICLE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              {/* 담당 직원 (v2.93.0) — 이 값이 법인카드 작성 화면의 사용대상·비고 기본값이 된다.
                  ⭐한 직원은 한 대만 — 차를 바꿨으면 옛 차의 담당을 먼저 비운다(서버가 막고 이유를 준다). */}
              <label className="ui-label col-span-2">
                담당 직원 — 이 직원의 법인카드 <b>차량유지비</b> 행에 이 차 번호가 미리 들어갑니다
                <select className="ui-input ui-input-sm mt-0.5 w-full"
                  value={draft.assignedEmpId ?? ''}
                  onChange={(e) => setDraft({ ...draft, assignedEmpId: e.target.value || null })}>
                  <option value="">— 배정 없음 —</option>
                  {users.map((u) => (
                    <option key={u.userId} value={u.userId}>
                      {u.name}{u.department ? ` (${u.department})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ui-label col-span-2">
                지출내역 표기(별칭) — 쉼표로 구분. <b>글자가 정확히 같을 때만</b> 붙습니다
                <input className="ui-input ui-input-sm mt-0.5 w-full"
                  placeholder="6526, 카니발 174호 2716"
                  value={aliasText} onChange={(e) => setAliasText(e.target.value)} />
              </label>
              <label className="ui-label">
                정렬 순서 (작을수록 위)
                <input type="number" className="ui-input ui-input-sm mt-0.5 w-full"
                  value={draft.sortOrder}
                  onChange={(e) => setDraft({ ...draft, sortOrder: Number(e.target.value) || 0 })} />
              </label>
              <label className="ui-label">
                비고
                <input className="ui-input ui-input-sm mt-0.5 w-full"
                  value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
              </label>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button type="button" disabled={save.isPending || draft.name.trim() === ''}
                onClick={() => save.mutate()}
                className="btn btn-primary btn-sm">
                {save.isPending ? '저장 중…' : '저장'}
              </button>
              <button type="button" onClick={() => { setEditId(null); setErr(''); }}
                className="btn btn-secondary btn-sm">취소</button>
              {editId !== 0 && (
                <button type="button" disabled={remove.isPending}
                  onClick={() => remove.mutate(editId as number)}
                  className="btn btn-danger btn-sm ml-auto">
                  삭제
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── 안 붙은 표기 — 이 화면에서 제일 중요한 칸 ── */}
        <div>
          <h3 className="ui-subsection-title text-xs mb-1.5">어느 차에도 안 붙은 표기</h3>
          {unmatched.length === 0 ? (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
              ✅ 없습니다 — 차량유지비 전액이 등록 차량에 붙어 있습니다.
            </div>
          ) : (
            <div className="text-xs bg-amber-50 border border-amber-200 rounded px-3 py-2 space-y-1">
              <p className="text-amber-800">
                {/* 금액 열람 권한이 없으면 줄에 건수만 보이므로 문구도 '금액' 대신 '표기'로 (v2.84.0) */}
                아래 {canViewAmounts ? '금액은' : '표기는'} <b>차량별 집계에서 빠져 있습니다.</b> 해당
                차량의 [별칭]에 이 글자를 넣거나 차량을 새로 등록하세요.
              </p>
              {unmatched.map((u) => (
                <div key={u.target} className="flex justify-between gap-3 text-amber-900">
                  <span>「{u.target}」 <span className="text-amber-600">({u.reason})</span></span>
                  <span className="num">{u.amount != null && <>{fmt(u.amount)}원 </>}({u.count}건)</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {!canEdit && (
          <p className="text-xs text-slate-500">
            보기만 가능합니다 — 차량 기본정보는 경영지원부·admin 만 고칠 수 있습니다.
          </p>
        )}
      </div>
    </DraggableModal>
  );
}
